import type { Draft, DraftVertex } from "@/lib/draft";

/**
 * What one step on the outline did, in a few words — „Maß an Wand 2
 * gesetzt", „Ecke eingezogen" — for the tooltips on Rückgängig and
 * Wiederholen.
 *
 * Read off the two drafts, as `features/planning/undoLabel.ts` does for the
 * board and for the same reason: what the drafts differ in is what the step
 * did. The editor has few gestures, and each leaves its own trace — a notch
 * adds walls, „Wand entfernen" opens the ring or shortens the walk at an end,
 * „Ecke entfernen" drops one corner and moves none, a push moves the two
 * corners of one wall, „Kalibrieren" moves corners and no figure. `null`
 * where the trace fits none of them; the tooltip then says just the word.
 */
export function describeDraftStep(before: Draft, after: Draft): string | null {
  if (before.closed && !after.closed) return "Wand entfernt";
  if (!before.closed && after.closed) return "Umlauf geschlossen";

  const grown = after.walls.length - before.walls.length;
  if (grown > 0) return "Ecke eingezogen";
  if (grown < 0) return shortened(before, after);

  const moved = after.vertices.flatMap((vertex, at) => (sameSpot(vertex, before.vertices[at]) ? [] : [at]));
  if (moved.length === 0) return retyped(before, after);

  // A push moves both corners of the wall in hand and one of each neighbour's.
  const pushed = after.walls.flatMap((_, at) => (moved.includes(at) && moved.includes(at + 1) ? [at] : []));
  if (pushed.length === 1) return `Wand ${pushed[0]! + 1} verschoben`;
  const figuresKept = after.walls.every(
    (wall, at) => wall.lengthMm === before.walls[at]?.lengthMm && wall.label === before.walls[at]?.label,
  );
  return figuresKept ? "Skizze ausgerichtet" : null;
}

function sameSpot(a: DraftVertex | undefined, b: DraftVertex | undefined): boolean {
  return a !== undefined && b !== undefined && a.xMm === b.xMm && a.yMm === b.yMm;
}

/** The corners of a walk, the ring's seam once. */
function corners(draft: Draft): DraftVertex[] {
  return draft.closed ? draft.vertices.slice(0, -1) : draft.vertices;
}

/**
 * Fewer walls, the ring as it was: one corner gone and every other where it
 * stood is „Ecke entfernt" — or, at the loose end of an open walk,
 * „Wand entfernt". Anything else lost walls by a push laid flush.
 */
function shortened(before: Draft, after: Draft): string | null {
  const was = corners(before);
  const now = corners(after);
  const kept = (vertex: DraftVertex) => now.some((each) => sameSpot(each, vertex));
  const gone = was.filter((vertex) => !kept(vertex));
  if (gone.length === 1 && now.length === was.length - 1 && now.every((vertex) => was.some((each) => sameSpot(each, vertex)))) {
    const end = !before.closed && (sameSpot(gone[0], was[0]) || sameSpot(gone[0], was.at(-1)));
    return end ? "Wand entfernt" : "Ecke entfernt";
  }
  return "Wand verschoben";
}

/** The same corners: one wall's figure or label, typed. */
function retyped(before: Draft, after: Draft): string | null {
  const changed = after.walls.flatMap((wall, at) => {
    const was = before.walls[at];
    return was === undefined || (was.lengthMm === wall.lengthMm && was.label === wall.label) ? [] : [at];
  });
  if (changed.length !== 1) return null;
  const at = changed[0]!;
  const was = before.walls[at]!;
  const now = after.walls[at]!;
  if (was.label !== now.label && was.lengthMm === now.lengthMm) return `Beschriftung an Wand ${at + 1} geändert`;
  if (was.label !== now.label) return `Wand ${at + 1} geändert`;
  if (was.lengthMm === null) return `Maß an Wand ${at + 1} gesetzt`;
  if (now.lengthMm === null) return `Maß an Wand ${at + 1} entfernt`;
  return `Maß an Wand ${at + 1} geändert`;
}
